import { NavigateFunction } from "react-router-dom";
import { ThanksPageType } from "../../constants/enums";

/**
 * Options for navigating to ThanksPage
 */
interface NavigateToThanksPageOptions {
  /** Replace current history entry instead of pushing new one */
  replace?: boolean;
  /** Additional state to pass to the page */
  state?: Record<string, unknown>;
}

/**
 * Type guard to validate ThanksPageType enum values
 * @param value - String value to validate (null returns false)
 * @returns True if value is a valid ThanksPageType enum member
 */
export const isValidThanksPageType = (value: string | null): value is ThanksPageType => {
  if (!value) return false;
  return Object.values(ThanksPageType).includes(value as ThanksPageType);
};

/**
 * Navigation helper for redirecting to ThanksPage with specific type.
 * Uses query parameters as primary method to ensure type persists across refreshes.
 *
 * @param navigate - React Router navigate function
 * @param type - ThanksPageType enum value to display
 * @param options - Optional navigation configuration
 * @param options.replace - Replace current history entry instead of adding new one
 * @param options.state - Additional state to pass to the page
 *
 * @example
 * // Basic navigation
 * navigateToThanksPage(navigate, ThanksPageType.EKYC);
 *
 * @example
 * // Replace current entry with additional state
 * navigateToThanksPage(navigate, ThanksPageType.AGREEMENT, {
 *   replace: true,
 *   state: { agreementId: '123' }
 * });
 */
export const navigateToThanksPage = (
  navigate: NavigateFunction,
  type: ThanksPageType,
  options?: NavigateToThanksPageOptions
) => {
  // Runtime validation in development mode
  if (process.env.NODE_ENV === 'development' && !isValidThanksPageType(type)) {
    console.error(`[navigateToThanksPage] Invalid ThanksPageType: "${type}"`);
  }

  const { replace = false, state = {} } = options || {};

  // Reuse URL generation logic
  const url = getThanksPageUrl(type);

  // Also include in state as backup
  const navigationState = { type, ...state };

  navigate(url, { replace, state: navigationState });
};

/**
 * Helper to get thank you page URL with query parameters.
 *
 * @param type - ThanksPageType enum value
 * @param additionalParams - Optional additional query parameters to append
 * @returns The thank you page URL with query parameters
 *
 * @example
 * // Basic URL generation
 * const url = getThanksPageUrl(ThanksPageType.ONBOARDING);
 * // Returns: "/thanks?type=onboarding"
 *
 * @example
 * // With additional parameters
 * const url = getThanksPageUrl(ThanksPageType.EKYC, { source: 'email' });
 * // Returns: "/thanks?type=ekyc&source=email"
 */
export const getThanksPageUrl = (
  type: ThanksPageType,
  additionalParams?: Record<string, string>
): string => {
  const params = new URLSearchParams({
    type,
    ...(additionalParams || {}),
  });

  return `/thanks?${params.toString()}`;
};
